<script setup lang="ts">
import type { MediaLoadingStrategy } from 'vidstack'
import type { AudioPlayerVariant } from '~/models/Media.js'
import type { TeacherArticle } from '~/models/Content/TeacherArticle'
import { computed, provide, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { setTitle } from '~/utils/dom'
import useLicenseControlStore from '~/stores/licenseControl'
import useAuthStore from '~/stores/auth'
import { ContentType } from '~/models/Content/ContentType'
import { ColorName, ColorTheme } from '~/models/Content/Color'
import { useContentHelper } from '~/composables/useContentHelper'
import { useColorTheme } from '~/composables/useColorTheme.js'
import { useAppColor } from '~/composables/useAppColor'
import useContentApi from '~/api/contentApi'
import AuSpinner from '~/components/ui/AuSpinner.vue'
import TeacherContent from '~/components/subject/TeacherContent.vue'
import NotFound from '~/components/ResourceEngine/Components/NotFound.vue'

const props = defineProps<{ locationId: number }>()

useAppColor(ColorName.Orange10)

const { isTeacher } = storeToRefs(useAuthStore())
const { getTitle } = useContentHelper()
const { findContents } = useContentApi()

const {
  data: articles,
  isLoading: isLoadingArticle,
  isError
} = useQuery({
  staleTime: Infinity,
  queryKey: computed(() => ['teacher-article', props.locationId]),
  queryFn: async () => await findContents<TeacherArticle>({
    locationIdCriterion: [props.locationId],
    contentTypeCriterion: [ContentType.TeacherArticle],
  }),
  retry: 1,
})

const { style } = useColorTheme(ColorTheme.BlueOrange)

provide<AudioPlayerVariant>('embedAudioVariant', 'inline')
provide<MediaLoadingStrategy>('embedAudioLoad', 'visible')

watch(articles, async () => {
  if (!articles.value || !articles.value.length)
    return
  setTitle(getTitle(articles.value[0]))
  const { checkItemLicense } = useLicenseControlStore()
  await checkItemLicense(articles.value[0])
})
</script>

<template>
  <AuSpinner
    v-if="isLoadingArticle"
    center
  />
  <NotFound v-else-if="isError || !isTeacher || !articles?.length" />
  <TeacherContent
    v-else
    :locations="articles"
    :style
  />
</template>
